import { makeQuiz, normaliseRoomCode, TEAM_COLOURS, type RoomInfo, type TeamResult } from '@typing-racer/shared';
import { renderQuiz } from './screens/quiz.js';
import {
  accuracyOf,
  applyKey,
  createTypingState,
  COLOURS,
  DEFAULT_ROOM_OPTIONS,
  DEFAULT_VEHICLE,
  progressOf,
  rollingWpmOf,
  wpmOf,
  sanitiseName,
  type PlayerInfo,
  type PlayerProgress,
  type RaceResult,
  type RoomOptions,
  type ServerMessage,
  type TypingState,
  type LeagueRow,
  type RecentRace,
  analyseKeystrokes,
  LESSONS,
  languageLessons,
  LANGUAGES,
  lessonStars,
  SUBJECTS,
  subjectLessons,
  type Language,
  type Subject,
  type UserStats,
  summarise,
  type Lesson,
} from '@typing-racer/shared';
import { Music, MUSIC_TRACKS, Synth, TYPING_SOUNDS, type MusicTrack, type TypingSound } from './audio.js';
import { loadLessonProgress, nextLesson, saveLessonResult } from './lesson.js';
import { renderLessonResult, renderLessons } from './screens/lessons.js';
import { renderLanguages } from './screens/languages.js';
import { renderSubjects } from './screens/subjects.js';
import { Account } from './account.js';
import { coachingLines, renderStats } from './screens/stats.js';
import { renderAbout } from './screens/about.js';
import { loadAffiliate } from './screens/affiliate.js';
import { clear, h } from './dom.js';
import { GameScene, VIEW_MODES } from './game/scene.js';
import { getStyle, saveStyle, type StyleId } from './game/style.js';
import { LocalTransport, WsTransport, type Transport } from './net/client.js';
import { Hud, renderCountdown } from './screens/hud.js';
import { renderLobby } from './screens/lobby.js';
import { renderLeague, renderResults } from './screens/results.js';
import { attachTyping } from './typing/input.js';

type Screen = 'lobby' | 'countdown' | 'race' | 'results' | 'league' | 'lessons' | 'lesson' | 'lessonResult' | 'languages' | 'subjects' | 'stats' | 'about' | 'quiz';

const PREF = 'typing-racer.prefs';
interface Prefs {
  name: string;
  vehicle: string;
  colour: string;
}
function loadPrefs(): Prefs {
  try {
    const p = JSON.parse(localStorage.getItem(PREF) ?? '{}') as Partial<Prefs>;
    return { name: p.name ?? '', vehicle: p.vehicle ?? DEFAULT_VEHICLE, colour: p.colour ?? COLOURS[0]!.hex };
  } catch {
    return { name: '', vehicle: DEFAULT_VEHICLE, colour: COLOURS[0]!.hex };
  }
}

class App {
  ui = document.getElementById('ui')!;
  scene = new GameScene(document.getElementById('game')!);
  synth = new Synth();
  music = new Music();
  account = new Account();
  authMode: 'none' | 'login' | 'register' = 'none';
  authBusy = false;
  stats: UserStats | null = null;
  /** Coaching lines for the results screen, once stats are fetched after a race. */
  coaching: { title: string; body: string }[] | null = null;
  lesson: Lesson | null = null;
  /** Set when the current lesson came from the language picker. */
  language: Language | null = null;
  subject: Subject | null = null;
  lessonResult: { summary: ReturnType<typeof summarise>; stars: 1 | 2 | 3; hints: ReturnType<typeof analyseKeystrokes> } | null = null;
  prefs = loadPrefs();
  transport: Transport | null = null;
  myId: string | null = null;
  joined = false;
  status = 'Not connected';
  players: PlayerInfo[] = [];
  options: RoomOptions = { ...DEFAULT_ROOM_OPTIONS };
  progress = new Map<string, PlayerProgress>();
  typing: TypingState | null = null;
  results: RaceResult[] = [];
  lastTeams: TeamResult[] | null = null;
  quizOn = loadQuizPref();
  quizResult: { score: number; total: number } | null = null;
  /** Sentences of the race just finished, for the recall quiz. */
  raceSentences: string[] = [];
  /** A lobby update arrived while the quiz was showing; apply it afterwards. */
  private pendingLobby = false;
  private detachQuiz: (() => void) | null = null;
  room: RoomInfo | null = null;
  /** Room code from an invite link, until acted on. */
  invite: string | null = parseInvite();
  /** How the next connection should enter: the public room, a new private room, or a code. */
  private entry: { kind: 'public' } | { kind: 'create' } | { kind: 'code'; code: string } = { kind: 'public' };
  /** Per-tab id that survives a refresh, so the server can hand our seat back. */
  private sessionId = getSessionId();
  private reconnecting = false;
  league: { rows: LeagueRow[]; recent: RecentRace[] } = { rows: [], recent: [] };
  screen: Screen = 'lobby';
  prevScreen: Screen = 'lobby';
  hud: Hud | null = null;
  detachTyping: (() => void) | null = null;
  raceStartAt = 0;
  finishedPlace: number | null = null;
  countdownEl: HTMLElement | null = null;
  countdownTimer: ReturnType<typeof setInterval> | null = null;
  racing = false;

  constructor() {
    this.scene.reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.scene.onFrame = () => this.frame();
    void this.scene.setPreview(this.prefs.vehicle, this.prefs.colour);
    // Browsers only allow audio after a gesture; start any saved music track on the first one.
    const kick = () => {
      this.music.play();
      removeEventListener('pointerdown', kick);
      removeEventListener('keydown', kick);
    };
    addEventListener('pointerdown', kick);
    addEventListener('keydown', kick);
    void this.account.verify().then(() => this.render());
    void loadAffiliate().then((ads) => this.scene.setAds(ads));
    // A refreshed tab goes straight back to its room; the server may still hold its seat.
    const seat = loadSeat();
    if (seat && sanitiseName(this.prefs.name)) {
      this.entry = seat;
      this.join(new WsTransport());
    }
    this.scene.onBoardClick = (board) => {
      if (board.url) window.open(board.url, '_blank', 'noopener');
      else this.toast(board.kind === 'advertise' ? 'Want your brand on this track? Contact the site owner.' : 'Link unavailable', false);
    };
    this.render();
  }

  // ----------------------------------------------------------- account
  async submitAuth(name: string, password: string): Promise<void> {
    if (this.authBusy) return;
    this.authBusy = true;
    this.render();
    try {
      if (this.authMode === 'register') await this.account.register(name, password);
      else await this.account.login(name, password);
      this.prefs.name = this.account.name ?? this.prefs.name;
      this.savePrefs();
      this.authMode = 'none';
      this.toast(`Signed in as ${this.account.name}`, false);
    } catch (e) {
      this.toast(e instanceof Error ? e.message : 'Sign in failed');
    } finally {
      this.authBusy = false;
      this.render();
    }
  }

  async showStats(): Promise<void> {
    if (!this.account.token) return this.toast('Sign in to see your progress.');
    try {
      this.stats = await this.account.stats();
    } catch (e) {
      return this.toast(e instanceof Error ? e.message : 'Could not load stats');
    }
    if (this.screen !== 'stats') this.prevScreen = this.screen;
    this.screen = 'stats';
    this.render();
  }

  /** After a race: send practice results if offline, then fetch stats for coaching. */
  async afterRace(results: RaceResult[]): Promise<void> {
    if (!this.account.token) return;
    const me = results.find((r) => r.id === this.myId);
    if (this.transport?.kind === 'local') await this.account.postPractice(results);
    try {
      this.stats = await this.account.stats();
      this.coaching = coachingLines(this.stats, me);
      if (this.screen === 'results') this.render();
    } catch {
      /* stats are optional */
    }
  }

  viewName(): string {
    return VIEW_MODES.find((v) => v.id === this.scene.view)?.name ?? 'Behind';
  }
  musicName(): string {
    return this.music.enabled ? (MUSIC_TRACKS.find((t) => t.id === this.music.track)?.name ?? 'On') : 'Off';
  }
  cycleView(): void {
    this.scene.cycleView();
    this.hud?.setViewName(this.viewName());
    if (this.screen === 'lobby') this.render();
  }
  toggleMusic(): void {
    this.setMusicOn(!this.music.enabled);
  }
  setMusicOn(on: boolean): void {
    this.music.setEnabled(on);
    this.music.play();
    this.hud?.setMusicName(this.musicName());
    if (this.screen === 'lobby') this.render();
  }
  setMusic(t: MusicTrack): void {
    this.music.setTrack(t);
    this.music.play();
    this.hud?.setMusicName(this.musicName());
    if (this.screen === 'lobby') this.render();
  }
  typingName(): string {
    return TYPING_SOUNDS.find((t) => t.id === this.synth.typingSound)?.name ?? 'Off';
  }
  setTypingSound(t: TypingSound): void {
    this.synth.setTypingSound(t);
    this.hud?.setTypingName(this.typingName());
    if (this.screen === 'lobby') this.render();
  }
  cycleTypingSound(): void {
    const i = TYPING_SOUNDS.findIndex((t) => t.id === this.synth.typingSound);
    this.setTypingSound(TYPING_SOUNDS[(i + 1) % TYPING_SOUNDS.length]!.id);
  }
  setSfx(on: boolean): void {
    this.synth.setMuted(!on);
    // Engine may never have started if effects were off when the race began.
    if (on && (this.screen === 'race' || this.screen === 'lesson')) this.synth.startEngine();
    this.hud?.setSfx(on);
    if (this.screen === 'lobby') this.render();
  }
  hudOptions(mode: 'race' | 'lesson', title?: string) {
    return {
      mode,
      ...(title ? { title } : {}),
      viewName: this.viewName(),
      musicName: this.musicName(),
      sfxOn: !this.synth.muted,
      typingName: this.typingName(),
      onView: () => this.cycleView(),
      onMusic: () => this.toggleMusic(),
      onSfx: () => this.setSfx(this.synth.muted),
      onTyping: () => this.cycleTypingSound(),
      onQuit: () => (mode === 'lesson' ? this.showLessons() : this.leave()),
    };
  }

  savePrefs(): void {
    try {
      localStorage.setItem(PREF, JSON.stringify(this.prefs));
    } catch {
      /* ignore */
    }
  }

  // ----------------------------------------------------------- transport
  connect(t: Transport): void {
    this.transport?.send({ type: 'leave' });
    this.transport?.close();
    this.transport = t;
    t.onStatus((s) => {
      if (this.transport !== t) return;
      this.status = t.kind === 'local' ? 'Offline practice' : s === 'open' ? 'Connected' : s === 'connecting' ? 'Connecting…' : 'Reconnecting…';
      if (t.kind === 'online' && s === 'closed' && this.joined) {
        // Keep the screen; the server holds our seat for a while and we re-join on reopen.
        this.reconnecting = true;
        this.toast('Connection lost. Reconnecting…');
      } else if (t.kind === 'online' && s === 'open' && this.reconnecting) {
        this.reconnecting = false;
        this.sendJoin(t);
      }
      this.render();
    });
    t.onMessage((m) => { if (this.transport === t) this.onMessage(m); });
    t.connect();
    this.sendJoin(t);
  }

  private sendJoin(t: Transport): void {
    const base = { name: this.prefs.name, vehicle: this.prefs.vehicle, colour: this.prefs.colour, session: this.sessionId, ...(this.account.token ? { token: this.account.token } : {}) };
    if (t.kind !== 'online') return t.send({ type: 'join', ...base });
    // After a private room exists, re-joins go by its code rather than creating another.
    const entry = this.entry.kind === 'create' && this.room && !this.room.isPublic ? { kind: 'code' as const, code: this.room.code } : this.entry;
    if (entry.kind === 'create') t.send({ type: 'createRoom', ...base });
    else if (entry.kind === 'code') t.send({ type: 'join', ...base, room: entry.code });
    else t.send({ type: 'join', ...base });
    saveSeat(entry.kind === 'create' ? null : entry);
  }

  onMessage(m: ServerMessage): void {
    switch (m.type) {
      case 'welcome':
        this.myId = m.id;
        this.joined = true;
        this.scene.setLocal(m.id);
        break;
      case 'lobby':
        this.players = m.players;
        this.options = m.options;
        this.room = m.room;
        if (this.screen === 'quiz' && m.phase === 'lobby') {
          this.pendingLobby = true;
          void this.syncVehicles();
          break;
        }
        if (this.transport?.kind === 'online') {
          // Keep the room in the URL so a refresh or a shared address lands back here.
          const wanted = m.room.isPublic ? '' : `#room=${m.room.code}`;
          if (location.hash !== wanted) history.replaceState(null, '', `${location.pathname}${wanted}`);
          if (!m.room.isPublic) this.invite = null;
          saveSeat(m.room.isPublic ? { kind: 'public' } : { kind: 'code', code: m.room.code });
        }
        if (m.phase === 'lobby' && this.screen !== 'league') this.toLobby();
        void this.syncVehicles();
        break;
      case 'countdown':
        this.raceSentences = m.sentences;
        this.startCountdown(m.sentences, m.startAt, m.tip);
        break;
      case 'go':
        this.go();
        break;
      case 'resume':
        this.resumeRace(m);
        break;
      case 'progress':
        for (const p of m.players) {
          this.progress.set(p.id, p);
          const v = this.scene.getVehicle(p.id);
          if (v) v.target = p.progress;
        }
        break;
      case 'playerFinished':
        if (m.id === this.myId) {
          this.finishedPlace = m.place;
          this.synth.finish();
          this.synth.stopEngine();
        }
        break;
      case 'results':
        this.raceSentences = m.sentences;
        this.lastTeams = m.teams;
        this.showResults(m.results);
        break;
      case 'league':
        this.league = { rows: m.rows, recent: m.recent };
        if (this.screen === 'league') this.render();
        break;
      case 'error':
        this.toast(m.message);
        break;
      case 'pong':
        break;
    }
  }

  async syncVehicles(): Promise<void> {
    const ids = new Set(this.players.map((p) => p.id));
    for (const p of this.players) {
      const lane = this.players.indexOf(p);
      // In team mode every car wears its team colour so the field reads at a glance.
      const colour = p.team !== null && this.options.teams > 0 ? (TEAM_COLOURS[p.team] ?? p.colour) : p.colour;
      await this.scene.addVehicle(p.id, p.vehicle, colour, lane);
    }
    for (const id of this.progress.keys()) if (!ids.has(id)) this.progress.delete(id);
    for (const id of this.scene.vehicleIds()) if (!ids.has(id)) this.scene.removeVehicle(id);
  }

  // ----------------------------------------------------------- flow
  toLobby(): void {
    this.detachQuiz?.();
    this.detachQuiz = null;
    this.pendingLobby = false;
    this.racing = false;
    this.detachTyping?.();
    this.detachTyping = null;
    this.hud?.destroy();
    this.hud = null;
    this.clearCountdown();
    this.synth.stopEngine();
    this.typing = null;
    this.progress.clear();
    this.finishedPlace = null;
    this.scene.resetProgress();
    this.scene.mode = 'preview';
    void this.scene.setPreview(this.prefs.vehicle, this.prefs.colour);
    this.screen = 'lobby';
    this.render();
  }

  startCountdown(sentences: string[], startAt: number, tip: string): void {
    this.clearCountdown();
    this.hud?.destroy();
    this.typing = createTypingState(sentences, {
      sentenceCount: this.options.sentenceCount,
      typoStallMs: this.options.typoStallMs,
      streakLength: this.options.streakLength,
      streakBonusChars: this.options.streakBonusChars,
    });
    this.progress.clear();
    this.finishedPlace = null;
    this.scene.hidePreview();
    this.scene.resetProgress();
    this.scene.lookAhead = 5;
    this.scene.race();
    this.screen = 'countdown';
    this.raceStartAt = startAt;
    clear(this.ui);
    this.hud = new Hud(this.ui, this.hudOptions('race'));
    this.hud.update(this.hudModel());
    let last = -1;
    const tick = () => {
      const remaining = Math.ceil((startAt - Date.now()) / 1000);
      const n = Math.max(0, remaining);
      if (n !== last) {
        last = n;
        this.countdownEl?.remove();
        this.countdownEl = renderCountdown(this.ui, n === 0 ? 'GO!' : n, tip);
        this.synth.countdown(n === 0);
      }
    };
    tick();
    this.countdownTimer = setInterval(tick, 100);
  }

  /** Back in a race after a refresh or a dropped connection. */
  private resumeRace(m: Extract<ServerMessage, { type: 'resume' }>): void {
    this.raceSentences = m.sentences;
    this.startCountdown(m.sentences, m.startAt, 'Welcome back! Your place was held.');
    if (this.typing) {
      const t = this.typing;
      const st = m.state;
      t.sentenceIndex = st.sentenceIndex;
      t.cursor = st.cursor;
      t.committedChars = st.committedChars;
      t.bonusChars = st.bonusChars;
      t.errorBuffer = st.errorBuffer;
      t.streak = st.streak;
      t.streaksCompleted = st.streaksCompleted;
      t.stallUntil = st.stallUntil;
      t.correctCount = st.correctCount;
      t.errorCount = st.errorCount;
      t.backspaceCount = st.backspaceCount;
      t.startedAt = st.startedAt;
      t.finishedAt = st.finishedAt;
      if (st.sentenceIndex >= m.sentences.length) t.finishedAt = t.finishedAt ?? Date.now();
    }
    this.finishedPlace = m.state.place;
    const wait = m.startAt - Date.now();
    if (m.phase === 'racing' || wait <= 0) this.go();
    else setTimeout(() => this.screen === 'countdown' && this.go(), wait);
    this.updateHud();
    if (this.myId) {
      const v = this.scene.getVehicle(this.myId);
      if (v && this.typing) v.snap(progressOf(this.typing));
    }
    this.toast('Welcome back! Your place was held.', false);
  }

  clearCountdown(): void {
    if (this.countdownTimer) clearInterval(this.countdownTimer);
    this.countdownTimer = null;
    this.countdownEl?.remove();
    this.countdownEl = null;
  }

  go(): void {
    this.screen = 'race';
    this.racing = true;
    setTimeout(() => this.clearCountdown(), 700);
    this.synth.startEngine();
    this.detachTyping?.();
    this.detachTyping = attachTyping((key) => this.onKey(key));
  }

  onKey(key: string): void {
    if (key === 'Tab') return this.cycleView();
    if (key === 'Escape') return this.screen === 'lesson' ? this.showLessons() : this.leave();
    if (this.screen === 'lesson') return this.onLessonKey(key);
    if (!this.racing || !this.typing || this.finishedPlace) return;
    const ev = applyKey(this.typing, key, Date.now());
    this.transport?.send({ type: 'key', key, t: Date.now() });
    switch (ev) {
      case 'correct':
        this.synth.keyPress();
        break;
      case 'sentenceDone':
        this.synth.keyPress(true);
        break;
      case 'backspace':
        this.synth.keyPress();
        break;
      case 'error':
        this.synth.error();
        this.hud?.shake();
        if (this.myId) this.scene.getVehicle(this.myId)?.stall(this.typing.config.typoStallMs);
        break;
      case 'nitro':
        this.synth.keyPress();
        this.synth.nitro();
        this.hud?.flashNitro();
        if (this.myId) this.scene.getVehicle(this.myId)?.nitro();
        break;
      case 'finished':
        this.synth.keyPress(true);
        break;
    }
    // Local prediction: move our own car immediately, the server confirms shortly after.
    if (this.myId) {
      const v = this.scene.getVehicle(this.myId);
      if (v) v.target = Math.max(v.target, progressOf(this.typing));
    }
    this.updateHud();
  }

  updateHud(): void {
    if (!this.hud || !this.typing) return;
    const now = Date.now();
    const finished = this.finishedPlace !== null;
    this.hud.update({
      ...this.hudModel(),
      // Freeze the speedometer on the final figure once we cross the line.
      rollingWpm: finished ? wpmOf(this.typing, now) : rollingWpmOf(this.typing, now),
      stalled: !finished && now < this.typing.stallUntil,
    });
  }

  frame(): void {
    if (this.screen !== 'race' && this.screen !== 'countdown' && this.screen !== 'lesson') return;
    const local = this.myId ? this.scene.getVehicle(this.myId) : undefined;
    this.synth.setEngine(local ? Math.min(1, Math.abs(local.speed) / 25) : 0);
    this.updateHud();
  }

  hudModel() {
    return {
      myId: this.myId,
      players: this.players,
      progress: this.progress,
      typing: this.typing,
      rollingWpm: 0,
      accuracy: this.typing ? accuracyOf(this.typing) : 1,
      stalled: false,
      finishedPlace: this.finishedPlace,
    };
  }

  showResults(results: RaceResult[]): void {
    this.racing = false;
    this.detachTyping?.();
    this.detachTyping = null;
    this.hud?.destroy();
    this.hud = null;
    this.clearCountdown();
    this.synth.stopEngine();
    this.results = results;
    this.coaching = null;
    this.quizResult = null;
    this.scene.mode = 'overview';
    void this.afterRace(results);
    const me = results.find((r) => r.id === this.myId);
    const questions = this.quizOn && me && !me.isBot ? makeQuiz(this.raceSentences, hashSeed(this.raceSentences)) : [];
    if (questions.length) {
      this.screen = 'quiz';
      clear(this.ui);
      this.detachQuiz = renderQuiz(
        this.ui,
        questions,
        (score) => {
          this.quizResult = { score, total: questions.length };
          this.finishQuiz();
        },
        () => this.finishQuiz(),
      );
      return;
    }
    this.screen = 'results';
    this.render();
  }

  private finishQuiz(): void {
    this.detachQuiz?.();
    this.detachQuiz = null;
    this.screen = 'results';
    this.render();
  }

  toast(msg: string, error = true): void {
    const t = h('div', { class: `toast${error ? '' : ' ok'}`, role: 'alert' }, msg);
    this.ui.append(t);
    setTimeout(() => t.remove(), 3500);
  }

  // ----------------------------------------------------------- render
  render(): void {
    if (this.screen === 'race' || this.screen === 'countdown' || this.screen === 'lesson' || this.screen === 'quiz') return;
    clear(this.ui);
    const online = this.transport?.kind === 'online';
    if (this.screen === 'results') {
      renderResults(
        this.ui,
        this.results,
        this.myId,
        online,
        {
          again: () => {
            if (this.pendingLobby) {
              this.pendingLobby = false;
              this.toLobby();
            } else this.transport?.send({ type: 'backToLobby' });
          },
          league: () => this.showLeague(),
          leave: () => this.leave(),
          ...(this.account.token ? { progress: () => void this.showStats() } : {}),
        },
        this.coaching,
        online ? this.lastTeams : null,
        this.quizResult,
      );
      return;
    }
    if (this.screen === 'stats' && this.stats) {
      renderStats(this.ui, this.stats, () => {
        this.screen = this.prevScreen === 'stats' ? 'lobby' : this.prevScreen;
        this.render();
      });
      return;
    }
    if (this.screen === 'about') {
      renderAbout(this.ui, () => {
        this.screen = 'lobby';
        this.render();
      });
      return;
    }
    if (this.screen === 'lessons') {
      const progress = loadLessonProgress();
      renderLessons(this.ui, LESSONS, progress, nextLesson(LESSONS, progress), {
        start: (l) => this.startLesson(l),
        back: () => {
          this.screen = 'lobby';
          this.render();
        },
      });
      return;
    }
    if (this.screen === 'lessonResult' && this.lesson && this.lessonResult) {
      const list = this.language ? languageLessons(this.language) : this.subject ? subjectLessons(this.subject) : LESSONS;
      const i = list.findIndex((l) => l.id === this.lesson!.id);
      const next = list[i + 1] ?? null;
      const lesson = this.lesson;
      renderLessonResult(this.ui, lesson, this.lessonResult.summary, this.lessonResult.stars, this.lessonResult.hints, next, {
        retry: () => this.startLesson(lesson),
        next: () => next && this.startLesson(next),
        list: () => (this.language ? this.showLanguages(this.language) : this.subject ? this.showSubjects(this.subject) : this.showLessons()),
      });
      return;
    }
    if (this.screen === 'subjects') {
      renderSubjects(this.ui, loadLessonProgress(), this.subject, {
        select: (sub) => this.showSubjects(sub),
        start: (lesson) => this.startLesson(lesson),
        back: () => {
          this.subject = null;
          this.screen = 'lobby';
          this.render();
        },
      });
      return;
    }
    if (this.screen === 'languages') {
      renderLanguages(this.ui, loadLessonProgress(), this.language, {
        select: (l) => this.showLanguages(l),
        start: (lesson) => this.startLesson(lesson),
        back: () => {
          this.language = null;
          this.screen = 'lobby';
          this.render();
        },
      });
      return;
    }
    if (this.screen === 'league') {
      renderLeague(this.ui, this.league.rows, this.league.recent, this.prefs.name, () => {
        this.screen = this.prevScreen === 'league' ? 'lobby' : this.prevScreen;
        this.render();
      });
      return;
    }
    renderLobby(
      this.ui,
      {
        joined: this.joined,
        online,
        status: this.status,
        myId: this.myId,
        name: this.prefs.name,
        vehicle: this.prefs.vehicle,
        colour: this.prefs.colour,
        style: getStyle(),
        muted: this.synth.muted,
        musicOn: this.music.enabled,
        engineOn: this.synth.engineOn,
        quizOn: this.quizOn,
        music: this.music.track,
        typing: this.synth.typingSound,
        view: this.scene.view,
        players: this.players,
        options: this.options,
        room: this.room,
        invite: this.invite,
        account: this.account.name,
        authMode: this.authMode,
        authBusy: this.authBusy,
      },
      {
        setName: (n) => {
          this.prefs.name = n;
          this.savePrefs();
        },
        setVehicle: (v) => this.setVehicle(v, this.prefs.colour),
        setColour: (c) => this.setVehicle(this.prefs.vehicle, c),
        setStyle: (s) => this.setStyle(s),
        setMuted: (m) => this.setSfx(!m),
        setMusic: (t) => this.setMusic(t),
        setMusicOn: (on) => this.setMusicOn(on),
        setQuizOn: (on) => {
          this.quizOn = on;
          try {
            localStorage.setItem('typing-racer.quiz', on ? '1' : '0');
          } catch {
            /* ignore */
          }
          this.render();
        },
        setEngineOn: (on) => {
          this.synth.setEngineOn(on);
          if (on && (this.screen === 'race' || this.screen === 'lesson')) this.synth.startEngine();
          this.render();
        },
        setTypingSound: (t) => this.setTypingSound(t),
        setView: (v) => {
          this.scene.setView(v);
          this.render();
        },
        lessons: () => this.showLessons(),
        languages: () => this.showLanguages(null),
        subjects: () => this.showSubjects(null),
        joinOnline: () => {
          this.entry = { kind: 'public' };
          this.join(new WsTransport());
        },
        createRoom: () => {
          this.entry = { kind: 'create' };
          this.join(new WsTransport());
        },
        joinRoom: (raw) => {
          const code = normaliseRoomCode(raw);
          if (!code) {
            this.invite = null;
            history.replaceState(null, '', location.pathname);
            if (raw.trim()) this.toast('Room codes are 4 letters or numbers.');
            this.render();
            return;
          }
          this.entry = { kind: 'code', code };
          this.join(new WsTransport());
        },
        copyLink: () => this.copyInvite(),
        setTeam: (team) => this.transport?.send({ type: 'setTeam', team }),
        practice: () => this.join(new LocalTransport()),
        leave: () => this.leave(),
        ready: (r) => this.transport?.send({ type: 'ready', ready: r }),
        start: () => this.transport?.send({ type: 'start' }),
        setOptions: (o) => this.transport?.send({ type: 'setOptions', options: o }),
        showLeague: () => this.showLeague(),
        showAbout: () => {
          this.screen = 'about';
          this.render();
        },
        showStats: () => void this.showStats(),
        setAuthMode: (mode) => {
          this.authMode = mode;
          this.render();
        },
        submitAuth: (n, p) => void this.submitAuth(n, p),
        signOut: () => {
          void this.account.logout().then(() => this.render());
          this.render();
        },
      },
    );
  }

  join(t: Transport): void {
    if (this.account.name) this.prefs.name = this.account.name;
    const name = sanitiseName(this.prefs.name);
    if (!name) return this.toast('Enter a name of 2 to 16 letters or numbers.');
    this.prefs.name = name;
    this.savePrefs();
    this.connect(t);
  }

  copyInvite(): void {
    if (!this.room || this.room.isPublic) return;
    const link = `${location.origin}${location.pathname}#room=${this.room.code}`;
    const done = () => this.toast(`Invite link copied: ${link}`, false);
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(link).then(done, () => window.prompt('Copy this invite link', link));
    else window.prompt('Copy this invite link', link);
  }

  leave(): void {
    this.lesson = null;
    this.reconnecting = false;
    saveSeat(null);
    this.room = null;
    this.lastTeams = null;
    history.replaceState(null, '', location.pathname);
    this.transport?.send({ type: 'leave' });
    this.transport?.close();
    this.transport = null;
    this.joined = false;
    this.myId = null;
    this.players = [];
    this.status = 'Not connected';
    this.scene.clearVehicles();
    this.toLobby();
  }

  setVehicle(vehicle: string, colour: string): void {
    this.prefs.vehicle = vehicle;
    this.prefs.colour = colour;
    this.savePrefs();
    void this.scene.setPreview(vehicle, colour);
    this.transport?.send({ type: 'setVehicle', vehicle, colour });
    this.render();
  }

  async setStyle(s: StyleId): Promise<void> {
    saveStyle(s);
    this.render();
    await this.scene.setStyle(s);
  }

  // ----------------------------------------------------------- lessons
  showLessons(): void {
    if (this.joined) this.leave();
    this.endLessonSession();
    this.language = null;
    this.subject = null;
    this.scene.clearVehicles();
    this.scene.mode = 'preview';
    void this.scene.setPreview(this.prefs.vehicle, this.prefs.colour);
    this.screen = 'lessons';
    this.render();
  }

  showSubjects(sub: Subject | null): void {
    if (this.joined) this.leave();
    this.endLessonSession();
    this.subject = sub;
    this.language = null;
    this.scene.clearVehicles();
    this.scene.mode = 'preview';
    void this.scene.setPreview(this.prefs.vehicle, this.prefs.colour);
    this.screen = 'subjects';
    this.render();
  }

  showLanguages(lang: Language | null): void {
    if (this.joined) this.leave();
    this.endLessonSession();
    this.subject = null;
    this.language = lang;
    this.scene.clearVehicles();
    this.scene.mode = 'preview';
    void this.scene.setPreview(this.prefs.vehicle, this.prefs.colour);
    this.screen = 'languages';
    this.render();
  }

  startLesson(lesson: Lesson): void {
    if (this.joined) this.leave();
    this.endLessonSession();
    this.lesson = lesson;
    this.lessonResult = null;
    this.typing = createTypingState(lesson.lines, {
      sentenceCount: lesson.lines.length,
      typoStallMs: 400,
      streakLength: 1_000_000, // no nitro in lessons: steady practice
      streakBonusChars: 0,
      ...(lesson.accentInsensitive ? { accentInsensitive: true } : {}),
    });
    if (lesson.id.startsWith('lang-')) this.language = LANGUAGES.find((l) => lesson.id.startsWith(`lang-${l.id}-`)) ?? this.language;
    if (lesson.id.startsWith('subj-')) this.subject = SUBJECTS.find((x) => lesson.id.startsWith(`subj-${x.id}-`)) ?? this.subject;
    this.finishedPlace = null;
    this.myId = 'me';
    this.scene.clearVehicles();
    this.scene.hidePreview();
    this.scene.setLocal('me');
    void this.scene.addVehicle('me', this.prefs.vehicle, this.prefs.colour, 2).then((v) => v.snap(0));
    this.scene.lookAhead = 0;
    this.scene.race();
    this.screen = 'lesson';
    clear(this.ui);
    this.hud = new Hud(this.ui, this.hudOptions('lesson', lesson.title));
    this.hud.meanings = lesson.meanings ?? null;
    this.updateHud();
    this.detachTyping?.();
    this.detachTyping = attachTyping((key) => this.onKey(key));
    this.synth.startEngine();
    this.music.play();
  }

  onLessonKey(key: string): void {
    if (!this.typing || !this.lesson || this.finishedPlace) return;
    const ev = applyKey(this.typing, key, Date.now());
    if (ev === 'error') {
      this.synth.keyPress();
      this.synth.error();
      this.hud?.shake();
      this.scene.getVehicle('me')?.stall(this.typing.config.typoStallMs);
    } else if (ev !== 'ignored') this.synth.keyPress(ev === 'sentenceDone' || ev === 'finished');
    const v = this.scene.getVehicle('me');
    if (v) v.target = progressOf(this.typing);
    this.updateHud();
    if (ev === 'finished') {
      this.finishedPlace = 1;
      const summary = summarise(this.typing, Date.now());
      const stars = lessonStars(this.lesson, summary.wpm, summary.accuracy);
      saveLessonResult(this.lesson, summary.wpm, summary.accuracy, stars);
      void this.account.postLesson(this.lesson.id, summary.wpm, summary.accuracy, stars);
      this.lessonResult = { summary, stars, hints: analyseKeystrokes(this.typing.keystrokes, 2) };
      this.synth.finish();
      setTimeout(() => {
        if (this.screen !== 'lesson') return;
        this.endLessonSession(false);
        this.scene.mode = 'overview';
        this.screen = 'lessonResult';
        this.render();
        for (let i = 0; i < stars; i++) setTimeout(() => this.synth.star(), 300 + i * 250);
      }, 1200);
    }
  }

  private endLessonSession(clearLesson = true): void {
    this.detachTyping?.();
    this.detachTyping = null;
    this.hud?.destroy();
    this.hud = null;
    this.synth.stopEngine();
    this.typing = null;
    this.finishedPlace = null;
    if (clearLesson) this.lesson = null;
  }

  showLeague(): void {
    if (this.screen !== 'league') this.prevScreen = this.screen;
    this.screen = 'league';
    if (this.transport) this.transport.send({ type: 'requestLeague' });
    else void fetch('/api/league').then((r) => r.json()).then((l: { rows: LeagueRow[]; recent: RecentRace[] }) => {
      this.league = l;
      if (this.screen === 'league') this.render();
    }).catch(() => undefined);
    this.render();
  }
}

type Seat = { kind: 'public' } | { kind: 'code'; code: string };
function saveSeat(seat: Seat | null): void {
  try {
    if (seat) sessionStorage.setItem('typing-racer.seat', JSON.stringify(seat));
    else sessionStorage.removeItem('typing-racer.seat');
  } catch {
    /* ignore */
  }
}
function loadSeat(): Seat | null {
  try {
    const s = JSON.parse(sessionStorage.getItem('typing-racer.seat') ?? 'null') as Seat | null;
    return s && (s.kind === 'public' || (s.kind === 'code' && typeof s.code === 'string')) ? s : null;
  } catch {
    return null;
  }
}
/** Private per-tab reconnect credential, separate from public IDs used before v1.0.10. */
function freshReconnectSecret(): string {
  return Array.from(crypto.getRandomValues(new Uint8Array(32)), b => b.toString(16).padStart(2, '0')).join('');
}
function getSessionId(): string {
  const key = 'typing-racer.reconnect-secret-v2';
  try {
    let secret = sessionStorage.getItem(key);
    if (!secret || !/^[a-f0-9]{64}$/.test(secret)) {
      secret = freshReconnectSecret();
      sessionStorage.setItem(key, secret);
    }
    return secret;
  } catch {
    return freshReconnectSecret();
  }
}

function loadQuizPref(): boolean {
  try {
    return localStorage.getItem('typing-racer.quiz') !== '0';
  } catch {
    return true;
  }
}

/** Stable seed from the race sentences so everyone gets the same questions. */
function hashSeed(sentences: string[]): number {
  let h = 2166136261;
  for (const ch of sentences.join('|')) h = Math.imul(h ^ ch.charCodeAt(0), 16777619) >>> 0;
  return h;
}

/** Invite links look like /#room=ABCD (also accepts ?room=ABCD and #/room/ABCD). */
function parseInvite(): string | null {
  const h = location.hash.replace(/^#\/?/, '');
  const m = /(?:^|[?&/])room[=/]([A-Za-z0-9]{4})/.exec(h) ?? /(?:^|[?&])room=([A-Za-z0-9]{4})/.exec(location.search);
  return m ? normaliseRoomCode(m[1]!) : null;
}

const app = new App();
(window as unknown as { app: App }).app = app;
